import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronDown, ListTodo, Settings, Images, FileText, Microscope, ArrowLeft, ClipboardList } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useUserProfile } from '@/hooks/profile/useUserProfile';
import { getTabSettings } from '@/hooks/project/use-project-settings';

interface MobileProjectTabDropdownProps {
  projectId: string;
  goalId?: string;
  agentThreadId?: string;
  project?: any;
  goal?: any;
}

const MobileProjectTabDropdown: React.FC<MobileProjectTabDropdownProps> = ({
  projectId,
  goalId,
  agentThreadId,
  project,
  goal
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useUserProfile();
  
  // Get tab settings from project
  const tabSettings = useMemo(() => {
    return getTabSettings(project?.settings);
  }, [project?.settings]);
  
  // Determine active tab from current route
  const getActiveTab = () => {
    const pathSegments = location.pathname.split('/');
    const lastSegment = pathSegments[pathSegments.length - 1];
    
    if (location.pathname.includes('/page/')) return 'page';
    if (lastSegment === 'settings') return 'settings';
    if (lastSegment === 'services') return 'work_order';
    if (lastSegment === 'images') return 'images';
    if (lastSegment === 'resources') return 'resources';
    if (lastSegment === 'pages') return 'pages';
    return 'tasks'; // default
  };
  
  const activeTab = getActiveTab();
  
  // Get current tab info for display
  const getCurrentTabInfo = () => {
    switch (activeTab) {
      case 'tasks':
        return { icon: ListTodo, label: 'Tasks', color: 'from-purple-500/90 to-blue-500/90' };
      case 'pages':
        return { icon: FileText, label: 'Documents', color: 'from-purple-500/90 to-blue-500/90' };
      case 'resources':
        return { icon: Microscope, label: 'Research', color: 'from-purple-500/90 to-blue-500/90' };
      case 'images':
        return { icon: Images, label: 'Images', color: 'from-purple-500/90 to-blue-500/90' };
      case 'work_order':
        return { icon: ClipboardList, label: 'Work Order', color: 'from-purple-500/90 to-blue-500/90' };
      case 'page':
        return { icon: FileText, label: 'Page Editor', color: 'from-purple-500/90 to-blue-500/90' };
      case 'settings':
        return { icon: Settings, label: 'Settings', color: 'from-purple-500/90 to-blue-500/90' };
      default:
        return { icon: ListTodo, label: 'Tasks', color: 'from-purple-500/90 to-blue-500/90' };
    }
  };
  
  const currentTab = getCurrentTabInfo();
  
  // Navigation handlers
  const handleTasksClick = () => navigate(`/project/${projectId}/tasks`);
  const handlePagesClick = () => navigate(`/project/${projectId}/pages`);
  const handleImagesClick = () => navigate(`/project/${projectId}/images`);
  const handleResourcesClick = () => navigate(`/project/${projectId}/resources`);
  const handleServicesClick = () => navigate(`/project/${projectId}/services`);
  const handleSettingsClick = () => navigate(`/project/${projectId}/settings`);
  const handleBackClick = () => navigate('/project-manager');
  
  return (
    <div className="flex items-center gap-2 bg-white/50 dark:bg-gray-700/30 backdrop-blur-sm border border-purple-100/40 dark:border-purple-800/30 rounded-xl shadow-lg shadow-purple-500/10">
      
      {/* Main Navigation Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <motion.button
            className={cn(
              "flex-1 flex items-center justify-between px-4 py-2 rounded-lg",
              "border border-transparent backdrop-blur-sm",
              "bg-gradient-to-br from-purple-500/90 to-blue-500/90 text-white border-purple-300/50 dark:border-purple-600/50"
            )}
            whileHover={{ scale: 1.02, y: -1 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <div className="flex items-center gap-2">
              <currentTab.icon className="h-4 w-4" />
              <span className="text-sm font-medium">{currentTab.label}</span>
            </div>
            <ChevronDown className="h-4 w-4" />
          </motion.button>
        </DropdownMenuTrigger>
        
        <DropdownMenuContent 
          align="start" 
          className="w-64 bg-white/90 dark:bg-black/90 backdrop-blur-md border border-purple-200/50 dark:border-purple-700/50 shadow-xl"
          sideOffset={4}
        >
          {tabSettings.tasks && (
            <DropdownMenuItem
              onClick={handleTasksClick}
              className={cn(
                "flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors",
                activeTab === 'tasks' 
                  ? "bg-gradient-to-br from-purple-500/20 to-blue-500/20 text-purple-700 dark:text-purple-300" 
                  : "hover:bg-purple-50 dark:hover:bg-purple-900/20"
              )}
            >
              <ListTodo className="h-4 w-4" />
              <span>Tasks</span>
            </DropdownMenuItem>
          )}
          
          {tabSettings.documents && (
            <DropdownMenuItem
              onClick={handlePagesClick}
              className={cn(
                "flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors",
                activeTab === 'pages' 
                  ? "bg-gradient-to-br from-purple-500/20 to-blue-500/20 text-purple-700 dark:text-purple-300" 
                  : "hover:bg-purple-50 dark:hover:bg-purple-900/20"
              )}
            >
              <FileText className="h-4 w-4" />
              <span>Documents</span>
            </DropdownMenuItem>
          )}
          
          {tabSettings.research && (
            <DropdownMenuItem
              onClick={handleResourcesClick}
              className={cn(
                "flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors",
                activeTab === 'resources' 
                  ? "bg-gradient-to-br from-purple-500/20 to-blue-500/20 text-purple-700 dark:text-purple-300" 
                  : "hover:bg-purple-50 dark:hover:bg-purple-900/20"
              )}
            >
              <Microscope className="h-4 w-4" />
              <span>Research</span>
            </DropdownMenuItem>
          )}
          
          {tabSettings.images && (
            <DropdownMenuItem
              onClick={handleImagesClick}
              className={cn(
                "flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors",
                activeTab === 'images' 
                  ? "bg-gradient-to-br from-purple-500/20 to-blue-500/20 text-purple-700 dark:text-purple-300" 
                  : "hover:bg-purple-50 dark:hover:bg-purple-900/20"
              )}
            >
              <Images className="h-4 w-4" />
              <span>Images</span>
            </DropdownMenuItem>
          )}
          
          {tabSettings.work_order && (
            <DropdownMenuItem
              onClick={handleServicesClick}
              className={cn(
                "flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors",
                activeTab === 'work_order' 
                  ? "bg-gradient-to-br from-purple-500/20 to-blue-500/20 text-purple-700 dark:text-purple-300" 
                  : "hover:bg-purple-50 dark:hover:bg-purple-900/20"
              )}
            >
              <ClipboardList className="h-4 w-4" />
              <span>Work Order</span>
            </DropdownMenuItem>
          )}
          
          <DropdownMenuSeparator className="bg-purple-200/50 dark:bg-purple-700/50" />
          
          <DropdownMenuItem
            onClick={handleSettingsClick}
            className={cn(
              "flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors",
              activeTab === 'settings' 
                ? "bg-gradient-to-br from-purple-500/20 to-blue-500/20 text-purple-700 dark:text-purple-300" 
                : "hover:bg-purple-50 dark:hover:bg-purple-900/20"
            )}
          >
            <Settings className="h-4 w-4" />
            <span>Settings</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default MobileProjectTabDropdown; 